import { useEffect, useMemo, useState } from "react";
import { Sparkles, Copy, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import type { ParentAnalyticsResult } from "@/hooks/useParentAnalytics";
import { deriveActivityState } from "@/lib/parent/activityState";
import { ParentStatusChip } from "@/components/parent/shared/parentPrimitives";

/**
 * Phase 2 — Parent Briefing.
 *
 * Truth-in-labeling: the card is always titled "Parent Briefing". The
 * "AI-generated" chip only appears when the response is actually backed by
 * the model (source = ai_generated | cached_ai). Otherwise it shows
 * "Rule-based" or "Insight building" and renders a deterministic summary
 * computed locally — no fake AI badge ever.
 */

interface Props {
  analytics: ParentAnalyticsResult;
  childId: string;
  childName: string;
  periodLabel: string;
  periodStart: Date;
  periodEnd: Date;
}

type Source = "ai_generated" | "cached_ai" | "rule_based" | "insufficient_data";

interface Summary {
  overall: string;
  going_well: string[];
  needs_review: string[];
  focus: string[];
  generated_at: string;
}

function ruleBasedSummary(a: ParentAnalyticsResult, child: string, period: string): Summary {
  const first = child.split(" ")[0] || child;
  const now = Date.now();
  const activity = deriveActivityState({
    childName: child,
    lessonsTotal: a.lessonCount,
    milestonesTotal: a.lessonActivity.length,
    quizzesTotal: a.quizAttempts.length,
    lessonsLast7d: a.lessonActivity.filter((l) => now - new Date(l.date).getTime() <= 7 * 86400000).length,
    lessonsLast30d: a.lessonActivity.filter((l) => now - new Date(l.date).getTime() <= 30 * 86400000).length,
    quizzesLast30d: a.quizAttempts.filter((q) => now - new Date(q.completedAt).getTime() <= 30 * 86400000).length,
    daysSinceLastActivity: a.daysSinceLastActivity,
  });

  if (activity.state === "empty") {
    return {
      overall: activity.summaryLine,
      going_well: [],
      needs_review: [],
      focus: ["Set aside a 15-minute first session this week.", "Choose a topic together that feels interesting."],
      generated_at: new Date().toISOString(),
    };
  }
  if (activity.state === "reengage") {
    const focus: string[] = ["Spend 15 minutes on HeirWise tonight together."];
    a.actionRecommendations.slice(0, 2).forEach((r) => focus.push(r.title));
    return {
      overall: activity.summaryLine,
      going_well: a.strengths.slice(0, 2).map((s) => s.label + (s.detail ? ` — ${s.detail}` : "")),
      needs_review: a.needsReview.slice(0, 2).map((n) => n.label + (n.detail ? ` — ${n.detail}` : "")),
      focus,
      generated_at: new Date().toISOString(),
    };
  }

  const trend = a.periodDelta.averageQuizScore > 1 ? "improving" : a.periodDelta.averageQuizScore < -1 ? "declining" : "steady";
  const strongest = a.strongestTopic?.topic;
  const weakest = a.weakestTopic?.topic;
  const overall = activity.hasQuizSignal
    ? `${first} has completed ${a.lessonCount} lesson${a.lessonCount === 1 ? "" : "s"} in the ${period} with an average quiz score of ${a.averageQuizScore.toFixed(0)}% (${trend} vs prior). ${strongest ? `Strongest area appears to be ${strongest}. ` : ""}${weakest && a.weakestTopic && a.weakestTopic.averageQuizScore < 60 ? `${weakest} may benefit from review.` : ""}`.trim()
    : activity.summaryLine;

  const going_well: string[] = [];
  if (a.firstAttemptPassRate >= 75 && a.quizAttempts.length > 0) going_well.push(`Strong first-attempt pass rate (${Math.round(a.firstAttemptPassRate)}%).`);
  if (a.currentStreak >= 3) going_well.push(`${a.currentStreak}-day learning streak.`);
  a.strengths.slice(0, 3).forEach((s) => going_well.push(s.label + (s.detail ? ` — ${s.detail}` : "")));

  const needs_review: string[] = [];
  a.needsReview.slice(0, 4).forEach((n) => needs_review.push(n.label + (n.detail ? ` — ${n.detail}` : "")));
  a.riskFlags.slice(0, 2).forEach((r) => needs_review.push(r.label));

  const focus: string[] = [];
  a.actionRecommendations.slice(0, 3).forEach((r) => focus.push(r.title));
  if (focus.length === 0) focus.push("Keep encouraging consistent weekly sessions.");

  return { overall, going_well, needs_review, focus, generated_at: new Date().toISOString() };
}

function summaryToText(s: Summary, child: string): string {
  const lines: string[] = [`Parent Briefing for ${child}`, "", s.overall];
  if (s.going_well.length) { lines.push("", "What's Going Well:"); s.going_well.forEach((x) => lines.push(`• ${x}`)); }
  if (s.needs_review.length) { lines.push("", "What Needs Review:"); s.needs_review.forEach((x) => lines.push(`• ${x}`)); }
  if (s.focus.length) { lines.push("", "Suggested Parent Focus This Week:"); s.focus.forEach((x) => lines.push(`• ${x}`)); }
  return lines.join("\n");
}

/**
 * Stable activity hash — must change whenever inputs that would materially
 * change the briefing change. Used both for cache keys and to gate refresh.
 */
function computeActivityHash(a: ParentAnalyticsResult, childId: string, periodKey: string): string {
  const sig = [
    childId,
    periodKey,
    a.lessonCount,
    a.lessonActivity.length,
    a.quizAttempts.length,
    Math.round(a.averageQuizScore),
    Math.round(a.firstAttemptPassRate),
    a.betaPathCompleted,
    a.lastActiveDate ?? "",
    a.currentStreak,
    a.strongestTopic?.topic ?? "",
    a.weakestTopic?.topic ?? "",
  ].join("|");
  // Tiny non-crypto hash (fnv-1a) — adequate for cache discrimination.
  let h = 2166136261;
  for (let i = 0; i < sig.length; i++) {
    h ^= sig.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(16);
}

function isoDate(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}

export default function ParentBriefing({ analytics, childId, childName, periodLabel, periodStart, periodEnd }: Props) {
  const [source, setSource] = useState<Source>("rule_based");
  const [summary, setSummary] = useState<Summary>(() => ruleBasedSummary(analytics, childName, periodLabel));
  const [loading, setLoading] = useState(false);
  const [bumpKey, setBumpKey] = useState(0);

  const periodStartIso = useMemo(() => isoDate(periodStart), [periodStart]);
  const periodEndIso = useMemo(() => isoDate(periodEnd), [periodEnd]);
  const activityHash = useMemo(
    () => computeActivityHash(analytics, childId, `${periodStartIso}_${periodEndIso}`),
    [analytics, childId, periodStartIso, periodEndIso],
  );

  const sufficiency = analytics.dataSufficiency;

  // Always have a deterministic rule-based draft ready as fallback.
  useEffect(() => {
    setSummary(ruleBasedSummary(analytics, childName, periodLabel));
  }, [analytics, childName, periodLabel]);

  // Attempt AI generation when sufficiency allows.
  useEffect(() => {
    if (!childId) return;
    if (!sufficiency.aiEligible) {
      setSource(sufficiency.level === "empty" || sufficiency.level === "low" ? "insufficient_data" : "rule_based");
      return;
    }
    let cancelled = false;
    setLoading(true);
    const payload = {
      child_user_id: childId,
      period_start: periodStartIso,
      period_end: periodEndIso,
      sufficiency: { level: sufficiency.level, aiEligible: sufficiency.aiEligible },
      inputs: {
        childFirstName: childName.split(" ")[0] || childName,
        lessonCount: analytics.lessonCount,
        averageQuizScore: Math.round(analytics.averageQuizScore),
        firstAttemptPassRate: Math.round(analytics.firstAttemptPassRate),
        currentStreak: analytics.currentStreak,
        betaPathCompleted: analytics.betaPathCompleted,
        betaPathTotal: analytics.betaPathTotal,
        daysSinceLastActivity: analytics.daysSinceLastActivity,
        strongest: analytics.strongestTopic?.topic ?? null,
        weakest: analytics.weakestTopic?.topic ?? null,
        weakestAvg: analytics.weakestTopic ? Math.round(analytics.weakestTopic.averageQuizScore) : null,
        strengths: analytics.strengths.slice(0, 5).map((s) => s.label),
        needsReview: analytics.needsReview.slice(0, 5).map((n) => n.label),
        riskFlags: analytics.riskFlags.slice(0, 3).map((r) => r.label),
        actions: analytics.actionRecommendations.slice(0, 3).map((a) => a.title),
      },
      activity_hash: activityHash,
    };
    supabase.functions.invoke("parent-briefing", { body: payload })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error || !data) { setSource("rule_based"); return; }
        const src = (data).source as Source;
        if ((src === "ai_generated" || src === "cached_ai") && (data).summary) {
          setSummary((data).summary as Summary);
          setSource(src);
        } else {
          setSource(src ?? "rule_based");
        }
      })
      .catch(() => { if (!cancelled) setSource("rule_based"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [childId, analytics, activityHash, sufficiency.level, sufficiency.aiEligible, periodStartIso, periodEndIso, childName, bumpKey]);

  const updatedAt = new Date(summary.generated_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

  const chip = (() => {
    if (loading) return { tone: "calm" as const, label: "Generating" };
    if (source === "ai_generated") return { tone: "ready" as const, label: "AI-generated" };
    if (source === "cached_ai") return { tone: "ready" as const, label: "AI · cached" };
    if (source === "insufficient_data") return { tone: "calm" as const, label: "Insight building" };
    return { tone: "calm" as const, label: "Rule-based" };
  })();

  return (
    <section className="relative overflow-hidden rounded-3xl border border-white/[0.07] ring-1 ring-inset ring-white/[0.03] bg-[linear-gradient(180deg,hsl(var(--card)/0.78)_0%,hsl(var(--card)/0.5)_100%)] p-5 backdrop-blur-2xl shadow-[0_30px_72px_-40px_rgba(0,0,0,0.7),inset_0_1px_0_0_hsl(var(--foreground)/0.06)] sm:p-6">
      <div className="pointer-events-none absolute inset-x-10 top-0 h-px bg-gradient-to-r from-transparent via-cyan-400/25 to-transparent" />
      <div className="pointer-events-none absolute -top-24 right-1/4 h-48 w-48 rounded-full bg-cyan-500/[0.06] blur-[100px]" />
      <div className="relative">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="flex items-start gap-2.5 min-w-0">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-cyan-500/10 text-cyan-300 ring-1 ring-cyan-400/25">
              <Sparkles className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="font-heading text-base font-semibold text-foreground sm:text-lg">Parent Briefing</h2>
                <ParentStatusChip tone={chip.tone} label={chip.label} />
              </div>
              <p className="mt-0.5 text-xs text-muted-foreground sm:text-[13px]">A plain-English read on {childName.split(" ")[0]}'s progress and next best steps.</p>
            </div>
          </div>
          <div className="flex shrink-0 gap-1.5">
            <Button size="sm" variant="outline" onClick={() => setBumpKey((k) => k + 1)} disabled={loading} className="gap-1.5 h-8 text-[11px] border-white/10 bg-background/40 backdrop-blur"><RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Refresh</Button>
            <Button size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(summaryToText(summary, childName)).then(() => toast.success("Briefing copied"))} className="gap-1.5 h-8 text-[11px] border-white/10 bg-background/40 backdrop-blur"><Copy className="h-3 w-3" /> Copy</Button>
          </div>
        </div>

        {source === "insufficient_data" ? (
          <div className="rounded-2xl border border-cyan-400/15 bg-gradient-to-br from-cyan-500/[0.04] via-background/30 to-background/20 p-5">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-cyan-300/85 mb-2">Insight building</p>
            <p className="text-sm leading-relaxed text-foreground/90">
              The Parent Briefing sharpens as activity accumulates. Once {childName.split(" ")[0] || "your child"} completes a couple of lessons and a quiz, this card fills in with concept-specific observations and parent-facing language.
            </p>
            <p className="mt-3 text-[11.5px] text-muted-foreground/85">{sufficiency.reason}</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-2xl border border-white/[0.06] bg-background/30 p-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-cyan-300/85 mb-1.5">Overall</p>
              <p className="text-sm leading-relaxed text-foreground">{summary.overall}</p>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <Column title="Going Well" items={summary.going_well} accent="border-emerald-500/40" emptyText="Strengths will surface as quizzes are completed." />
              <Column title="Needs Review" items={summary.needs_review} accent="border-amber-500/40" emptyText="No review areas flagged yet." />
              <Column title="Focus This Week" items={summary.focus} accent="border-cyan-400/40" emptyText="—" />
            </div>
            <div className="flex items-center justify-between border-t border-white/[0.06] pt-3 text-[10px] text-muted-foreground">
              <span>From {childName.split(" ")[0]}'s analytics · {periodLabel}</span>
              <span>Updated {updatedAt}</span>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function Column({ title, items, accent, emptyText }: { title: string; items: string[]; accent: string; emptyText: string }) {
  return (
    <div className={`rounded-2xl border border-white/[0.05] border-l-2 ${accent} bg-background/35 px-3.5 py-3 backdrop-blur-md`}>
      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-2">{title}</p>
      {items.length === 0 ? (
        <p className="text-[11px] italic text-muted-foreground">{emptyText}</p>
      ) : (
        <ul className="space-y-1.5">
          {items.map((x, i) => <li key={i} className="text-xs text-foreground/95 leading-snug">• {x}</li>)}
        </ul>
      )}
    </div>
  );
}